import { useEffect, useState } from 'react';
import { Spinner, useToast } from '../components/ui';
import { api } from '../lib/api';
import { describePath, formatValue } from '../lib/paths';
import type { HistoryEntry } from '../lib/types';
import { useProjectContext } from './ProjectLayout';

const ACTION_LABEL: Record<HistoryEntry['action'], string> = {
  create: 'Created',
  edit: 'Edited',
  import: 'Imported',
  status: 'Status',
  publish: 'Published',
  restore: 'Restored',
  duplicate: 'Copied',
  rename: 'Renamed',
};

export function HistoryPage() {
  const { project, saving } = useProjectContext();
  const toast = useToast();
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (saving) return;
    api.history(project.id).then(setEntries).catch((e) => toast.push(e.message, true));
  }, [project.id, saving]);

  if (!entries) return <div className="row"><Spinner /> <span className="sub">Loading history…</span></div>;

  const q = filter.trim().toLowerCase();
  const shown = entries.filter((e) => !q
    || e.username.toLowerCase().includes(q)
    || e.summary.toLowerCase().includes(q)
    || e.changes.some((c) => describePath(c.path, project.content).toLowerCase().includes(q)));

  return (
    <div style={{ maxWidth: 980 }}>
      <div className="row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="section" style={{ marginBottom: 2 }}>Change history</h2>
          <div className="sub">Who changed what, and when. Edits made within two minutes of each other are grouped.</div>
        </div>
        <div style={{ flex: 1 }} />
        <input className="search" placeholder="Filter: a name, 'price', 'day 5'…" value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>

      {!shown.length && <div className="panel sub">No changes recorded{q ? ' for that filter' : ''}.</div>}

      <div className="timeline">
        {shown.map((entry) => (
          <div className="tl-item" key={entry.id}>
            <div className={`tl-dot ${entry.action}`} />
            <div className="tl-body">
              <div className="row" style={{ gap: 8 }}>
                <span className="tl-action">{ACTION_LABEL[entry.action]}</span>
                <span>{entry.summary}</span>
                <span className="sub">· {entry.username} · {new Date(entry.at).toLocaleString()}</span>
                {entry.changes.length > 0 && (
                  <button className="btn small ghost" onClick={() => setOpen(open === entry.id ? null : entry.id)}>
                    {open === entry.id ? 'Hide' : `Show ${entry.changes.length} change${entry.changes.length === 1 ? '' : 's'}`}
                  </button>
                )}
              </div>
              {open === entry.id && (
                <table className="table plain changes">
                  <thead><tr><th>Field</th><th>Before</th><th>After</th></tr></thead>
                  <tbody>
                    {entry.changes.map((c, i) => (
                      <tr key={i}>
                        <td>{describePath(c.path, project.content)}</td>
                        <td className="before">{formatValue(c.from)}</td>
                        <td className="after">{formatValue(c.to)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
